import type { QuoteRestriction } from "@freightsimple/generated-dashboard-openapi-client/models/QuoteRestriction";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import { QuoteRestrictionType } from "@freightsimple/generated-dashboard-openapi-client/models/QuoteRestrictionType";
import type { DropdownOption } from "@freightsimple/ui-core/Controls/Dropdown";
import { parseHours } from "../Validators/locationsErrorMessageForHours";
import { getEffectivePickupClose } from "../Validators/locationsErrorMessageForRestrictions";

/** Which end of a dock-hours window a value belongs to. */
export type LocationsHoursField = "openFrom" | "openUntil";

export const defaultHoursOptions: DropdownOption[] = [
  { value: "07:00", label: "7:00 AM" },
  { value: "07:30", label: "7:30 AM" },
  { value: "08:00", label: "8:00 AM" },
  { value: "08:30", label: "8:30 AM" },
  { value: "09:00", label: "9:00 AM" },
  { value: "09:30", label: "9:30 AM" },
  { value: "10:00", label: "10:00 AM" },
  { value: "10:30", label: "10:30 AM" },
  { value: "11:00", label: "11:00 AM" },
  { value: "11:30", label: "11:30 AM" },
  { value: "12:00", label: "12:00 PM" },
  { value: "12:30", label: "12:30 PM" },
  { value: "13:00", label: "1:00 PM" },
  { value: "13:30", label: "1:30 PM" },
  { value: "14:00", label: "2:00 PM" },
  { value: "14:30", label: "2:30 PM" },
  { value: "15:00", label: "3:00 PM" },
  { value: "15:30", label: "3:30 PM" },
  { value: "16:00", label: "4:00 PM" },
  { value: "16:30", label: "4:30 PM" },
  { value: "17:00", label: "5:00 PM" },
  { value: "17:30", label: "5:30 PM" },
  { value: "18:00", label: "6:00 PM" },
  { value: "18:30", label: "6:30 PM" },
];

function serviceRestrictionTooltip(description: string): string {
  return `${description} for this service`;
}

function formatTimeAmPm(time: string): string {
  const match = defaultHoursOptions.find((o) => o.value === time);
  return match?.label ?? time;
}

// oxlint-disable-next-line complexity, sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
export function getRestrictionViolation(
  hourTime: string,
  context: LocationContext,
  field: LocationsHoursField,
  restrictions: QuoteRestriction[],
  pickupOpenUntil: string | undefined,
): string | undefined {
  const hourValue = parseHours(hourTime);

  if (context === LocationContext.Pickup && field === "openFrom") {
    for (const r of restrictions) {
      if (r.type === QuoteRestrictionType.PickupOpenBefore && r.time) {
        if (hourValue >= parseHours(r.time)) {
          return serviceRestrictionTooltip(r.description);
        }
      }
    }
  }

  if (context === LocationContext.Delivery && field === "openUntil") {
    for (const r of restrictions) {
      if (r.type === QuoteRestrictionType.DeliveryCloseAfter && r.time) {
        if (hourValue < parseHours(r.time)) {
          return serviceRestrictionTooltip(r.description);
        }
      }

      if (
        r.type === QuoteRestrictionType.MinTransitWindowHours &&
        r.hours &&
        pickupOpenUntil
      ) {
        const effectiveClose = getEffectivePickupClose(
          pickupOpenUntil,
          restrictions,
        );
        const requiredUntil = parseHours(effectiveClose) + r.hours;
        if (hourValue < requiredUntil) {
          return `${serviceRestrictionTooltip(r.description)} (effective pickup close is ${formatTimeAmPm(effectiveClose)})`;
        }
      }
    }
  }

  return undefined;
}

export function buildHoursOptions(
  context: LocationContext,
  field: LocationsHoursField,
  restrictions: QuoteRestriction[] | undefined,
  pickupOpenUntil?: string,
): DropdownOption[] {
  return defaultHoursOptions.map((hour) => {
    const reason =
      restrictions && restrictions.length > 0
        ? getRestrictionViolation(
            hour.value,
            context,
            field,
            restrictions,
            pickupOpenUntil,
          )
        : undefined;

    if (reason) {
      // Surface the restriction reason as a browser-native tooltip on
      // the row. Avoids consuming the description sub-text slot, which
      // is reserved for option content.
      return {
        value: hour.value,
        label: hour.label,
        disabledReason: reason,
        disabled: true,
      };
    }

    return hour;
  });
}
